import { utf8ByteLength } from '@/shared/lib/utf8';
import { z } from 'zod';

const taskWorkTypeSchema = z.enum(['asset.segment.clip', 'asset.annotate', 'asset.review']);
export type TaskWorkType = z.infer<typeof taskWorkTypeSchema>;

const taskRequestStatusSchema = z.enum(['open', 'closed', 'cancelled']);
export type TaskRequestStatus = z.infer<typeof taskRequestStatusSchema>;

export type TaskExecutionStatus =
  | 'assigned'
  | 'in_progress'
  | 'completed'
  | 'expired'
  | 'cancelled'
  | 'reassigned';

type ActiveTaskExecutionStatus = Extract<TaskExecutionStatus, 'assigned' | 'in_progress'>;

export interface TaskAssigneeIdentity {
  principalId: string;
  displayName: string | null;
  email: string | null;
}

export interface ActiveTaskAssignment {
  executionId: string;
  status: ActiveTaskExecutionStatus;
  deadlineAtMs: number;
  assignee: TaskAssigneeIdentity;
}

const taskInputSchema = z
  .object({
    inputRole: z.enum(['source_asset', 'existing_annotation', 'submitted_annotation']),
    resourceType: z.enum(['asset', 'annotation']),
    resourceId: z.string().min(1),
  })
  .strict();

export type TaskInput = z.infer<typeof taskInputSchema>;

interface TaskResourceRef {
  resourceType: string;
  resourceId: string;
}

export interface TaskRequest {
  requestId: string;
  organizationId: string;
  projectId: string;
  workTypeId: TaskWorkType;
  title: string;
  instructions: string | null;
  status: TaskRequestStatus;
  createdBy: string;
  createdAtMs: number;
  closedBy: string | null;
  closedAtMs: number | null;
}

export interface TaskRequestListItem extends TaskRequest {
  activeAssignments: ActiveTaskAssignment[];
}

export interface TaskExecution {
  executionId: string;
  requestId: string;
  assigneeId: string;
  status: TaskExecutionStatus;
  assignedBy: string;
  assignedAtMs: number;
  deadlineAtMs: number;
  startedAtMs: number | null;
  endedAtMs: number | null;
  terminalReason: string | null;
  replacedByExecutionId: string | null;
}

interface TaskProgress {
  hasSavedProgress: boolean;
  lastProgressAtMs: number | null;
}

interface TaskAccessGrant {
  grantId: string;
  executionId: string;
  principalId: string;
  workTypeId: TaskWorkType;
  validUntilMs: number;
  revokedAtMs: number | null;
  revokeReason: string | null;
}

export interface TaskExecutionSummary extends TaskProgress {
  execution: TaskExecution;
}

export interface TaskRequestPage {
  items: TaskRequestListItem[];
  nextCursor: string | null;
}

export interface TaskRequestDetail {
  request: TaskRequest;
  inputs: TaskInput[];
  executions: TaskExecutionSummary[];
}

interface EligibleAssignee extends TaskAssigneeIdentity {
  activeExecutionCount: number;
}

export interface EligibleAssigneesResponse {
  items: EligibleAssignee[];
}

export interface AssignTaskResponse {
  execution: TaskExecution;
  grant: TaskAccessGrant;
  scopes: TaskResourceRef[];
}

export interface ReassignTaskResponse {
  replacedExecution: TaskExecution;
  replacementExecution: TaskExecution;
  replacementGrant: TaskAccessGrant;
  progress: TaskProgress;
}

export const createTaskRequestInputSchema = z
  .object({
    projectId: z.string().min(1),
    workTypeId: taskWorkTypeSchema,
    title: z.string().refine((value) => utf8ByteLength(value) >= 1 && utf8ByteLength(value) <= 200),
    instructions: z
      .string()
      .refine((value) => utf8ByteLength(value) >= 1 && utf8ByteLength(value) <= 10_000)
      .nullable()
      .optional(),
    inputs: z.array(taskInputSchema).min(1).max(2),
  })
  .strict()
  .superRefine((value, context) => {
    const sourceAssets = value.inputs.filter(
      (input) => input.inputRole === 'source_asset' && input.resourceType === 'asset',
    );
    const existingAnnotations = value.inputs.filter(
      (input) => input.inputRole === 'existing_annotation' && input.resourceType === 'annotation',
    );
    const submittedAnnotations = value.inputs.filter(
      (input) => input.inputRole === 'submitted_annotation' && input.resourceType === 'annotation',
    );
    const validByWorkType: Record<TaskWorkType, boolean> = {
      'asset.segment.clip':
        value.inputs.length === 1 &&
        sourceAssets.length === 1 &&
        existingAnnotations.length === 0 &&
        submittedAnnotations.length === 0,
      'asset.annotate':
        sourceAssets.length === 1 &&
        submittedAnnotations.length === 0 &&
        existingAnnotations.length <= 1 &&
        value.inputs.length === 1 + existingAnnotations.length,
      'asset.review':
        value.inputs.length === 2 &&
        sourceAssets.length === 1 &&
        submittedAnnotations.length === 1 &&
        existingAnnotations.length === 0,
    };
    if (!validByWorkType[value.workTypeId]) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'invalid_task_inputs' });
    }
  });
export type CreateTaskRequestInput = {
  projectId: string;
  workTypeId: 'asset.segment.clip' | 'asset.annotate' | 'asset.review';
  title: string;
  inputs: {
    inputRole: 'source_asset' | 'existing_annotation' | 'submitted_annotation';
    resourceType: 'asset' | 'annotation';
    resourceId: string;
  }[];
  instructions?: string | null | undefined;
};

export const taskRequestFormSchema = z
  .object({
    projectId: z.string().trim().min(1),
    workTypeId: taskWorkTypeSchema,
    title: z
      .string()
      .trim()
      .min(1)
      .refine((value) => utf8ByteLength(value) <= 200),
    instructions: z
      .string()
      .trim()
      .refine((value) => value.length === 0 || utf8ByteLength(value) <= 10_000),
    sourceAssetId: z.string().trim().min(1),
    annotationId: z.string().trim(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.workTypeId === 'asset.review' && !value.annotationId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['annotationId'],
        message: 'submitted_annotation_required',
      });
    }
  });
export type TaskRequestForm = {
  projectId: string;
  workTypeId: 'asset.segment.clip' | 'asset.annotate' | 'asset.review';
  title: string;
  instructions: string;
  sourceAssetId: string;
  annotationId: string;
};

export const taskCancellationInputSchema = z
  .object({
    reason: z
      .string()
      .trim()
      .min(1)
      .refine((value) => utf8ByteLength(value) <= 512),
  })
  .strict();

const taskRequestStatusFilterSchema = z.union([taskRequestStatusSchema, z.literal('all')]);
export type TaskRequestStatusFilter = 'open' | 'closed' | 'cancelled' | 'all';

export const taskAssignmentSearchSchema = z.object({
  projectId: z.string().min(1).optional().catch(undefined),
  status: taskRequestStatusFilterSchema.catch('open').default('open'),
  workTypeId: taskWorkTypeSchema.optional().catch(undefined),
});
export type TaskAssignmentSearch = {
  status: 'open' | 'closed' | 'cancelled' | 'all';
  projectId?: string | undefined;
  workTypeId?: 'asset.segment.clip' | 'asset.annotate' | 'asset.review' | undefined;
};

export interface TaskRequestFilters {
  projectId?: string;
  status?: TaskRequestStatus;
  workTypeId?: TaskWorkType;
}

export interface AssignTaskInput {
  requestId: string;
  assigneeId: string;
}

export interface ReassignTaskInput extends AssignTaskInput {
  executionId: string;
}

export interface CancelTaskExecutionInput {
  requestId: string;
  executionId: string;
  reason: string;
}

export interface CloseTaskRequestInput {
  requestId: string;
}

export interface CancelTaskRequestInput extends CloseTaskRequestInput {
  reason: string;
}

export type TaskAssigneePickerMode =
  | {
      kind: 'assign';
      requestId: string;
      unavailableAssigneeIds: string[];
    }
  | {
      kind: 'reassign';
      requestId: string;
      executionId: string;
      currentAssigneeId: string;
      unavailableAssigneeIds: string[];
      hasSavedProgress: boolean;
      lastProgressAtMs: number | null;
    };

export type TaskCancellationDialogMode =
  | { kind: 'execution'; requestId: string; executionId: string }
  | {
      kind: 'request';
      requestId: string;
      activeExecutionCount: number;
    };
